import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash'; // lodash import edildi
import { Alert, Button, Form, Input, Modal, notification, Select } from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import IdentityInput from 'components/form-elements/IdentityInput';
import roleService from 'services/role.services';
import { storage } from 'helpers';
import { AuthContext } from '../../../context';
import localRoles from '../../../helpers/roles';

const AddForm = ({
  visible,
  selectedUser,
  error: formError,
  submitting,
  handleCloseForm,
  handleSubmitForm,
}) => {
  const [form] = Form.useForm();
  const [roles, setRoles] = useState([]);
  const [rolesPending, setRolesPending] = useState(false);
  const [existError, setExistError] = useState(null);
  const context = useContext(AuthContext);
  const currentUser = storage?.getUserInfo();

  useEffect(() => {
    if (visible) {
      form.resetFields();
      getRoles();
    }
  }, [visible]);

  AddForm.propTypes = {
    visible: PropTypes.bool.isRequired,
    selectedUser: PropTypes.shape({
      email: PropTypes.string,
      role: PropTypes.shape({
        identifier: PropTypes.string,
      }),
      merchantInfos: PropTypes.arrayOf(
        PropTypes.shape({
          identifier: PropTypes.string,
        }),
      ),
    }),
    error: PropTypes.string,
    submitting: PropTypes.bool.isRequired,
    handleCloseForm: PropTypes.func.isRequired,
    handleSubmitForm: PropTypes.func.isRequired,
  };

  AddForm.defaultProps = {
    selectedUser: null,
    error: null,
  };

  const getRoles = async () => {
    const number = context?.selectedMerch?.number;
    try {
      setRolesPending(true);
      const { payload } = await roleService.getRoles(number);
      setRoles(payload);
      setRolesPending(false);
    } catch (err) {
      setRolesPending(false);
      console.log(err.message);
      notification.error({
        message: 'Hata',
        description: ' Roller yüklenirken hata oluştu. Lütfen sayfayı yenileyiniz.',
      });
    }
  };

  const handleSubmit = async () => {
    try {
      setExistError(null);
      const values = await form.validateFields();
      const user = storage.getUserInfo();
      const updatedValues = { ...values };

      if (updatedValues.selectedMerchs?.length > 0) {
        updatedValues.selectedMerchs.forEach((merch) => {
          const parsedMerch = JSON.parse(merch);
          if (updatedValues?.identityNumberList) {
            if (!Array.isArray(updatedValues.identityNumberList)) {
              updatedValues.identityNumberList = [];
            }
            updatedValues.identityNumberList.push(parsedMerch);
          }
        });
      }

      if (updatedValues.identityNumberList) {
        const uniqNumbers = new Set(
          updatedValues.identityNumberList.map((item) => JSON.stringify(item)),
        );
        updatedValues.identityNumberList = Array.from(uniqNumbers).map((item) => JSON.parse(item));
      }

      const body = {
        parentAccountId: user.identifier,
        ...updatedValues,
      };

      handleSubmitForm(user.identifier, body);
    } catch (err) {
      console.error('Validation Failed:', err);
    }
  };

  const renderMyMerchs = () => {
    const unSelectedMerchs = _.differenceBy(
      currentUser?.merchantInfos,
      selectedUser?.merchantInfos,
      'identifier',
    );
    return unSelectedMerchs?.map((merch) => (
      <Select.Option
        key={merch?.identifier}
        value={JSON.stringify({ number: merch.number, type: merch.type })}
      >
        {merch?.contactName} - {merch.number}
      </Select.Option>
    ));
  };

  const checkNumber = (rule, value) => {
    if (Number.isInteger(Number(value.number))) {
      if (value.type === 'VKN') {
        if (value.number.trim().length === 10) {
          return Promise.resolve();
        }
        return Promise.reject(new Error('Vergi Numarası 10 haneli olmalıdır.'));
      }
      if (value.type === 'TCKN') {
        if (value.number.trim().length === 11) {
          return Promise.resolve();
        }
        return Promise.reject(new Error('TC Numarası 11 haneli olmalıdır.'));
      }
      return Promise.reject(new Error('Zorunlu Alan'));
    }
    return Promise.reject(new Error('Sadece rakamlardan oluşmalıdır.'));
  };

  return (
    <Modal
      open={visible}
      footer={false}
      title="Kullanıcı İşlemleri"
      onCancel={handleCloseForm}
      destroyOnClose
    >
      <Form form={form} onFinish={handleSubmit} layout="vertical">
        <Form.Item
          name="email"
          label="E-Posta"
          initialValue={selectedUser?.email}
          rules={[
            { required: true, message: 'Zorunlu Alan' },
            { type: 'email', message: 'Geçersiz mail adresi!' },
          ]}
        >
          <Input className="input__default" placeholder="E-Posta" />
        </Form.Item>
        {currentUser?.role?.name === localRoles.LOCAL_ADMIN && (
          <>
            <Form.Item
              name="roleId"
              label="Kullanıcı Rolü"
              initialValue={selectedUser?.role?.identifier || ''}
              rules={[{ required: true, message: 'Zorunlu Alan' }]}
            >
              <Select loading={rolesPending} placeholder="Lütfen Rol Seçiniz">
                {roles?.map((role) => (
                  <Select.Option key={role.identifier} value={role.identifier}>
                    {role.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="selectedMerchs" label="Üye İşyerlerimden Seç">
              <Select mode="multiple" placeholder="Üye işyeri seçiniz">
                {renderMyMerchs()}
              </Select>
            </Form.Item>
            <Form.List name="identityNumberList" initialValue={selectedUser?.merchantInfos || []}>
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, fieldKey }) => (
                    <Form.Item key={key}>
                      <Form.Item
                        name={[name]}
                        validateTrigger={['onChange', 'onBlur']}
                        rules={[{ validator: checkNumber }]}
                        noStyle
                      >
                        <IdentityInput />
                      </Form.Item>
                      {fields.length > 1 && (
                        <MinusCircleOutlined
                          className="dynamic-delete-button indentity-remove"
                          onClick={() => remove(name)}
                        />
                      )}
                    </Form.Item>
                  ))}
                  <Form.Item>
                    <Button type="dashed" onClick={() => add()} style={{ width: '100%' }}>
                      <PlusOutlined /> Numara Ekle
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
            {(formError || existError) && (
              <Form.Item>
                <Alert message={formError || existError} type="error" showIcon />
              </Form.Item>
            )}
          </>
        )}
        <Form.Item className="text-align-right">
          <Button
            className="button button__outline mr-3"
            type="ghost"
            htmlType="button"
            disabled={submitting}
            onClick={handleCloseForm}
          >
            Vazgeç
          </Button>
          <Button
            className="button button__fill"
            type="primary"
            htmlType="submit"
            disabled={submitting}
            loading={submitting}
          >
            Kaydet
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddForm;
